import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// import CheckBox from '@mui/icons-material/CheckBox';
// import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import { ReactComponent as LocationIcon } from "../../icons/locationIcon.svg";
import { getChangedAttributes, countEffectivelyMetRequirements, resolveMembershipKeyFromQuery } from "../../../utils/helpers";
import { usStates, USStatesEnum, ProfileTab } from "../../../utils/enums";
import {
  useCompleteSignIn,
  useUpdateAccountDetails,
  useAddCredential,
  useLoadAccountMemberships,
  useLoadAccountSubscriptions,
} from "../../../hooks/authhooks";

import UserAvatar from "../../../components/Mobile/UserAvatar";
import {
  AccountSchema,
  CurrentRequirementsSchema,
  CredentialSchema,
  AddRequirementStatus,
  MembershipSchema,
  ResourceSchema,
} from "../../../data/types";

// import { useGetPaymentMethods } from '../../hooks/paymenthooks'
import { useGetMemberRequirementsStatus } from "../../../hooks/authhooks";
import {
  AddCredentialModal,
  CommunityResourcesModal,
  RequestMembershipModal,
} from "../../../molecules/Mobile/AppModal";
// import MembershipCard from "../../../components/Mobile/MembershipCard";
import MembershipCredentialsMobile from "../../../components/Mobile/MembershipCredentialsMobile";
import SubscriptionCard from "../../../components/Mobile/SubscriptionCard";
import MembershipCredentialsSkeleton from "../../../components/MembershipCredentialsSkeleton";
import ProfileInformationSkeleton from "../../../components/ProfileInformationSkeleton";
import MySubscriptionsSkeleton from "../../../components/MySubscriptionsSkeleton";
import { toast } from "sonner";

// type AddRequirementStatus = { status: boolean, requirement?: CurrentRequirementsSchema }

type ProfileTabId = "profile" | "memberships" | "subscriptions";

const getProfileTabFromPath = (pathname: string): ProfileTabId =>
  pathname === "/profile/memberships"
    ? "memberships"
    : pathname === "/profile/subscriptions"
    ? "subscriptions"
    : "profile";

type ProfileTabType = {
  id: ProfileTabId;
  details: ProfileTab;
  path: string;
};

const Profile = () => {
  const profileTabs: ProfileTabType[] = [
    { id: "profile", details: ProfileTab.ProfileInfo, path: "/profile" },
    {
      id: "memberships",
      details: ProfileTab.Membership,
      path: "/profile/memberships",
    },
    {
      id: "subscriptions",
      details: ProfileTab.Subscriptions,
      path: "/profile/subscriptions",
    },
  ];

  const { user, isLoadingUser, isFetchingUser } = useCompleteSignIn();
  const loggedinUsername = user && user?.username ? user.username : "";
  const loggedinUid = user?.uid ? user?.uid : "";
  const { updateAccount } = useUpdateAccountDetails(loggedinUid);

  const isProfileLoading = isLoadingUser || (isFetchingUser && !user);

  var {
    accountMemberships,
    isLoadingAccountMemberships,
    isFetchingAccountMemberships,
  } = useLoadAccountMemberships(loggedinUid);

  const isMembershipsLoading =
    isLoadingAccountMemberships || (isFetchingAccountMemberships && !accountMemberships);

  var {
    accountSubscriptions,
    isLoadingAccountSubscriptions,
    isFetchingAccountSubscriptions,
  } = useLoadAccountSubscriptions(loggedinUid);

  const isSubscriptionsLoading =
    isLoadingAccountSubscriptions || (isFetchingAccountSubscriptions && !accountSubscriptions);

  const { addMemberCredential } = useAddCredential(loggedinUid);
  // const { memberCredentials } = useGetCredentials(loggedin_username)

  const [profileDetails, setProfileDetails] = useState<AccountSchema>(
    user ? user : ({} as AccountSchema)
  );
  const [userAvatar, setUserAvatar] = useState<string>(
    user && user?.avatar ? user.avatar : ""
  );
  const [addingCredential, toggleAddingCredential] =
    useState<AddRequirementStatus>({ status: false });
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const currentLocation = location.pathname;

  const [searchParams] = useSearchParams();
  const membershipQuery = searchParams.get("id");

  const defaultTab = getProfileTabFromPath(currentLocation);
  const [activeTab, setActiveTab] = useState<ProfileTabId>(defaultTab);

  useEffect(() => {
    setActiveTab(getProfileTabFromPath(currentLocation));
  }, [currentLocation]);

  const { memberRequirementStatuses } =
    useGetMemberRequirementsStatus(loggedinUid);
  // console.log("memberRequirementsStatus", memberRequirementStatuses)

  // const defaultMemberRequirementsStatus = memberRequirementsStatus
  const [expandedMemberships, setExpandedMemberships] = useState<{
    [key: string]: {
      isExpanded: boolean;
      requirementsStatus: CurrentRequirementsSchema[];
      totalRequirementsCount: number;
      metRequirementsCount: number;
    };
  }>({});
  // console.log("expandedMemberships", expandedMemberships)

  const profileFirstName = profileDetails?.first_name ?? '';
  const profileLastName = profileDetails?.last_name ?? '';

  useEffect(() => {
    document.title = `Profile | ${profileFirstName} ${profileLastName}`.trim();

    // // Check if mobile view
    // const checkMobile = () => {
    //   setIsMobile(window.innerWidth < 768);
    // };

    // checkMobile();
    // window.addEventListener("resize", checkMobile);

    // return () => window.removeEventListener("resize", checkMobile);
  }, [profileFirstName, profileLastName]);

  useEffect(() => {
    if (accountMemberships && memberRequirementStatuses) {
      const defaultExpandedMembershipKey =
        activeTab === "memberships"
          ? resolveMembershipKeyFromQuery(membershipQuery, accountMemberships)
          : null;

      setExpandedMemberships((prev) => {
        const newExpandedMemberships = { ...prev };
        let hasChanges = false;

        accountMemberships?.forEach((membership) => {
          if (membership.community_id) {
            const requirementsStatus =
              memberRequirementStatuses[membership.community_id] || [];
            const totalRequirementsCount = requirementsStatus.filter(
              (item) => item.is_required === true
            ).length;
            const metRequirementsCount = countEffectivelyMetRequirements(requirementsStatus);

            const currentState =
              newExpandedMemberships[membership.community_id];
            const shouldExpandFromQuery =
              !!defaultExpandedMembershipKey &&
              membership.community_id === defaultExpandedMembershipKey;
            const newState = {
              isExpanded: currentState?.isExpanded ?? shouldExpandFromQuery,
              requirementsStatus,
              totalRequirementsCount,
              metRequirementsCount,
            };

            if (JSON.stringify(currentState) !== JSON.stringify(newState)) {
              hasChanges = true;
              newExpandedMemberships[membership.community_id] = newState;
            }
          }
        });

        return hasChanges ? newExpandedMemberships : prev;
      });
    }
  }, [accountMemberships, memberRequirementStatuses, activeTab, membershipQuery]);

  const handleUpdateAccount = async () => {
    let originalData = user ? user : ({} as AccountSchema);
    let changedData = getChangedAttributes(originalData, profileDetails);
    if (
      profileDetails &&
      profileDetails !== user &&
      changedData &&
      Object.keys(changedData).length > 0
    ) {
      try {
        await updateAccount(changedData);
        toast.success("Profile updated successfully", {
          description: "Your profile information has been saved.",
          duration: 5000,
          position: "top-right",
          className: "bg-green-500 text-white",
          icon: "🎉",
        });
      } catch {
        toast.error("Failed to update profile", {
          description: "Please try again.",
          duration: 5000,
          position: "top-right",
          className: "bg-red-500 text-white",
          icon: "🚫",
        });
      }
    }
  };

  const handleChangeInput = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.preventDefault();
    const { name, value } = e.target;
    if (name && (value || value === "")) {
      setProfileDetails((prev) => {
        return { ...prev, [name]: value };
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (name && files && files.length > 0) {
      let file_data = files[0];
      setProfileDetails((prev) => ({
        ...prev,
        [name]: file_data,
      }));
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const imgType = event.target.name;

    if (!file) {
      if (imgType === "avatar") {
        setUserAvatar("");
      }
      return;
    }

    // Read the selected image file as a data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (imgType === "avatar" && result) {
        setUserAvatar(result);
      }
      // Reset the input value after processing so the same file can be selected again
      if (avatarInputRef.current) {
        avatarInputRef.current.value = "";
      }
    };
    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      // Reset the input value even on error
      if (avatarInputRef.current) {
        avatarInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
    handleFileChange(event);
  };

  const handleRemoveAvatar = () => {
    setUserAvatar("");
    setProfileDetails((prev) => ({
      ...prev,
      avatar: "",
    }));
  };

  const first_name = profileDetails?.first_name
    ? profileDetails.first_name
    : "";
  const last_name = profileDetails?.last_name ? profileDetails.last_name : "";
  const address = profileDetails?.address ? profileDetails.address : "";
  const city = profileDetails?.city ? profileDetails.city : "";
  const state = profileDetails?.state ? profileDetails.state : "";
  const zipcode = profileDetails?.zipcode ? profileDetails.zipcode : "";

  // const { paymentMethods } = useGetPaymentMethods(loggedin_username)
  // const communityRequirements = accountMemberships?.map((membership) => membership.requirements)
  //   .flat()
  //   .filter((req): req is RequirementSchema => req !== undefined) || []
  // const { membershipRequirementsStatus } = useFetchRequirementsLookUp(communityRequirements, memberCredentials ? memberCredentials : [], paymentMethods)

  const handleSubmitCredential = (
    credential: CredentialSchema,
    communityId: string
  ) => {
    if (loggedinUsername && credential) {
      credential.username = loggedinUsername;
      addMemberCredential(communityId, credential);
    }
  };

  const toggleMembership = (communityId: string) => {
    setExpandedMemberships((prev) => ({
      ...prev,
      [communityId]: {
        ...prev[communityId],
        isExpanded: !prev[communityId]?.isExpanded,
      },
    }));
  };

  const [communityResourceID, setCommunityResourceID] = useState("");
  const [viewCommunityResources, setViewCommunityResources] = useState(false);
  const [membershipModal, toggleMembershipModal] = useState<null | "request" | "manage">(null);
  const [selectedMembership, setSelectedMembership] = useState<MembershipSchema | null>(null);
  // const [isMobile, setIsMobile] = useState(false);

  const handleSetViewCommunityResources = (
    communityId: string,
    isOpen: boolean
  ) => {
    setViewCommunityResources(isOpen);
    setCommunityResourceID(communityId);
  };

  const handleManageMembership = (membership: MembershipSchema) => {
    setSelectedMembership(membership);
    toggleMembershipModal("manage");
  };

  const membershipToResourceDetail = (membership: MembershipSchema): ResourceSchema => ({
    community_id: membership.community_id,
    id: membership.resource_id,
    name: membership.name,
    logo: membership.logo,
    description: membership.description,
    requires_membership: true,
  });

  // const handleSubmitMembershipRequest = async (
  //   _resource: ResourceSchema,
  //   _resourceType: ResourceType,
  //   _booking: ResourceBookingSchema
  // ) => false;

  // console.log("profileDetails", profileDetails)
  // console.log("accountMemberships", accountMemberships)
  return (
    <>
      <div className="w-full left-0 flex font-open-sans min-h-screen overflow-y-scroll relative top-0 justify-center bg-gradient-to-b from-[#FFFFFF] pb-6   backdrop-blur-2xl bg-[#FFFFFF] p-5">
        <div className="flex flex-col w-full  min-h-[1300px] sm:min-h-[2000px] relative items-start  bg-white">
          <div className="w-full mx-auto my-5">
            <p className="font-raleway text-[25px] text-black font-bold my-5">
              My Profile
            </p>

            {/* Tab Navigation */}
            <div className="flex flex-row gap-4 mb-3 overflow-scroll">
              {profileTabs.map((tab, i) => (
                <button
                  key={i}
                  className={`px-6 py-3  rounded-lg font-raleway font-normal w-auto text-[14px]  text-left whitespace-nowrap   ${
                    activeTab === tab.id
                      ? "bg-primaryDark-0 text-white font-semibold"
                      : "bg-white text-black border-[#E9E9E9]"
                  }`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(`${tab.path}`);
                  }}
                >
                  {tab.details}
                </button>
              ))}
            </div>

            {/* Profile Card */}
            {activeTab === "profile" && (
              isProfileLoading ? (
                <ProfileInformationSkeleton variant="mobile" />
              ) : (
              <>
              <p className="text-[18px] font-raleway text-gray-500 font-semibold mb-2 ">Profile Image</p>
                <div className="flex flex-col w-full rounded-xl mb-5  pb-5  bg-white">
                  <div className="grid grid-cols-2 rounded-2xl border-[1px] border-[#E9E9E9] bg-white" style={{ gridTemplateRows: 'minmax(120px, auto) auto' }}>
                    <div className="col-span-2 flex flex-row items-center p-5 min-h-[120px]">
                      <div className="flex-shrink-0 flex items-center justify-center mr-4">
                        {userAvatar ? (
                          <div className="flex border-[#E9E9E9] border-[1px] rounded-full h-[50px] w-[50px] items-center justify-center ">
                            <img
                              id="image-input-avatar"
                              src={userAvatar}
                              alt="User Avatar"
                              className="h-[50px] w-[50px] rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="flex border-[#E9E9E9] border-[1px] rounded-full h-[50px] w-[50px] items-center justify-center ">
                            <UserAvatar
                              firstName={user?.first_name}
                              lastName={user?.last_name}
                              username={user?.username}
                              email={user?.email}
                              className="h-[50px] w-[50px] m-auto font-open-sans rounded-full text-[30px]"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col"> 
                        <p className="text-[18px] text-black font-medium font-raleway">
                          Hi, {user && user?.first_name ? user.first_name : ""}
                        </p>
                        <p className="text-[14px] text-gray-600 font-normal font-raleway">
                          {user?.email || 'No email address yet'}
                        </p> 
                      </div>
                    </div>
                    <div className="flex flex-row  border-t-[1px] border-[#E9E9E9] gap-0 w-full col-span-2 py-3 px-2">
                      <label
                        htmlFor="image-input-avatar"
                        className="flex-1 flex items-center justify-center rounded-l-md py-3 px-2 text-[18px] font-raleway text-[#013847] hover:text-primaryDark-300 font-bold cursor-pointer border-r-[1px] border-[#E9E9E9] whitespace-nowrap min-w-0"
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        Upload Image
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="image-input-avatar"
                          name="avatar"
                          data-testid="avatar-input"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className={`flex-1 flex items-center justify-center border-none py-3 px-2 text-[18px] font-raleway font-medium whitespace-nowrap min-w-0 ${
                          userAvatar
                            ? "text-[#D42620] cursor-pointer"
                            : "text-[#B3B3B3]"
                        }`}
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>

                  {/* Profile Information Form */}
                  <div className="bg-white">
                    <div>
                      {/* <p className="text-[18px] font-raleway text-black font-semibold">Profile Information</p>
                    <p className="text-[14px] font-open-sans text-[#808080] mb-6 mt-2">
                      Add your first and last name to keep your profile up-to-date.
                    </p> */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleUpdateAccount();
                        }}
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="flex flex-col text-[16px] font-raleway text-gray-500 font-semibold mt-5">
                            First Name
                            <input
                              type="text"
                              name="first_name"
                              value={first_name}
                              onChange={handleChangeInput}
                              placeholder="Enter your first name"
                              className="border-[1px] border-[#E9E9E9] rounded-md my-2 h-[50px] text-[16px] text-[#808080] font-normal font-open-sans"
                            />
                          </div>
                          <div className="flex flex-col text-[16px] font-raleway text-gray-500 font-semibold">
                            Last Name
                            <input
                              type="text"
                              name="last_name"
                              value={last_name}
                              onChange={handleChangeInput}
                              placeholder="Enter your last name"
                              className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[16px] text-[#808080] font-normal font-open-sans"
                            />
                          </div>
                          <div className="flex flex-col text-[16px] font-raleway text-gray-500 font-semibold sm:col-span-2">
                            Address
                            <input
                              type="text"
                              name="address"
                              value={address}
                              onChange={handleChangeInput}
                              placeholder="Enter your address"
                              className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[16px] text-[#808080] font-normal font-open-sans"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:col-span-2">
                            <div className="flex flex-col text-[16px] font-raleway text-gray-500 font-semibold">
                              City
                              <input
                                type="text"
                                name="city"
                                value={city}
                                onChange={handleChangeInput}
                                placeholder="Enter your city"
                                className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[16px] text-[#808080] font-normal font-open-sans"
                              />
                            </div>
                            <div className="flex flex-col text-[16px] font-raleway text-gray-500 font-semibold">
                              State
                              <select
                                name="state"
                                value={state}
                                onChange={handleChangeInput}
                                className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[16px] text-[#808080] font-normal font-open-sans"
                              >
                                <option value="">
                                  -- Select your state --
                                </option>
                                {usStates &&
                                  usStates.length > 0 &&
                                  usStates.map((state: string, i: number) => (
                                    <option
                                      value={USStatesEnum[state]?.abbreviation}
                                      key={i}
                                    >
                                      {USStatesEnum[state]?.fullName}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="flex flex-col text-[16px] font-raleway text-gray-500 font-semibold">
                              Zipcode
                              <input
                                type="text"
                                name="zipcode"
                                value={zipcode}
                                onChange={handleChangeInput}
                                placeholder="Enter your zipcode"
                                className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[16px] text-[#808080] font-normal font-open-sans"
                              />
                            </div>
                          </div>
                        </div>
                        <button
                          type="submit"
                          className="mt-8 bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-[16px] rounded-md border-0 py-[12px] w-full"
                        >
                          Update Profile Information
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </>
              )
            )}

            {/* Membership Credentials Tab */}
            {activeTab === "memberships" && (
              <div className="my-5">
                {isMembershipsLoading ? (
                  <MembershipCredentialsSkeleton variant="mobile" />
                ) : (
                  <MembershipCredentialsMobile
                    memberships={accountMemberships || []}
                    expandedMemberships={expandedMemberships}
                    membershipQuery={membershipQuery}
                    toggleMembership={toggleMembership}
                    toggleAddingCredential={toggleAddingCredential}
                    handleSetViewCommunityResources={
                      handleSetViewCommunityResources
                    }
                    handleManageMembership={handleManageMembership}
                  />
                )}
              </div>
            )}
            {/* My Subscriptions Tab */}
            {activeTab === "subscriptions" && (
              isSubscriptionsLoading ? (
                <MySubscriptionsSkeleton variant="mobile" />
              ) : (
              <div className="my-5">
                <div className="flex justify-between items-center mb-4 min-w-0">
                  <p className="text-sm font-raleway text-[#4D4D4D]/90 font-medium truncate flex-1 mr-2">
                    Showing all subscriptions ({accountSubscriptions?.length || 0})
                  </p>
                </div>
                {Array.isArray(accountSubscriptions) && accountSubscriptions.length > 0 ? (
                  <div className="flex flex-col w-full gap-4">
                    {accountSubscriptions.map((subscription, i) => (
                      <SubscriptionCard
                        key={subscription.id || `${subscription.community_id}-${subscription.plan_id}-${i}`}
                        subscription={subscription}
                        onViewResources={(communityId) =>
                          handleSetViewCommunityResources(communityId, true)
                        }
                      />
                    ))}
                  </div>
                ) : (
                <div className="flex flex-col items-center justify-center min-h-[200px] border border-[#E9E9E9] rounded-xl p-6 bg-white">
                  <p className="text-[#8A8A8A] text-sm">
                    No subscriptions to show
                  </p>
                </div>
                )}
              </div>
              )
            )}
          </div>
        </div>
      </div>

      <CommunityResourcesModal
        isOpen={viewCommunityResources}
        communityId={communityResourceID}
        closeAppModal={() => setViewCommunityResources(false)}
      />
      <AddCredentialModal
        isOpen={addingCredential.status}
        closeAppModal={() => toggleAddingCredential({ status: false })}
        handleSubmitCredential={handleSubmitCredential}
        requirement={addingCredential.requirement as CurrentRequirementsSchema}
      />
      {selectedMembership && (
        <RequestMembershipModal
          formType={membershipModal}
          resourceDetail={membershipToResourceDetail(selectedMembership)}
          // handleSubmitRequest={handleSubmitMembershipRequest}
          closeAppModal={() => {
            toggleMembershipModal(null);
            setSelectedMembership(null);
          }}
        />
      )}
    </>
  );
};

export default Profile;
